import {useSearchParams} from 'react-router-dom';
import {useCallback, useMemo} from 'react';
import {SingleValue} from 'react-select';

interface BuildYearOption {
    label: string;
    value: number;
}
export function useBuildYears(buildYearFrom: number | undefined, buildYearTill: number | undefined) {
    const buildYearOptions: BuildYearOption[] = useMemo(() => {
        const thisYear = new Date().getFullYear();
        const firstYear = thisYear - 15;
        const years = Array.from({length: 16}).map((_, index) => firstYear + index);

        return years.map((year) => {
            return {
                label: year.toString(),
                value: year,
            };
        });
    }, []);

    const buildYearFromOptions = useMemo(() => {
        return buildYearOptions.filter((year) => {
            if (buildYearTill == null) {
                return true;
            }
            return buildYearTill >= year.value;
        });
    }, [buildYearOptions, buildYearTill]);

    const buildYearTillOptions = useMemo(() => {
        return buildYearOptions.filter((year) => {
            if (buildYearFrom == null) {
                return true;
            }
            return buildYearFrom <= year.value;
        });
    }, [buildYearOptions, buildYearFrom]);

    const selectedBuildYearFromOption = useMemo(() => {
        return buildYearFrom != null ? buildYearFromOptions.find((option) => option.value === buildYearFrom) : null;
    }, [buildYearFrom, buildYearFromOptions]);
    const selectedBuildYearTillOption = useMemo(() => {
        return buildYearTill != null ? buildYearTillOptions.find((option) => option.value === buildYearTill) : null;
    }, [buildYearTill, buildYearTillOptions]);

    const [, setSearchParams] = useSearchParams();
    const onSetBuildYearFromCallback = useCallback(
        (value: SingleValue<BuildYearOption>) => {
            setSearchParams((current) => {
                if (value?.value) {
                    current.set('build_year_from', value.value.toString());
                } else {
                    current.delete('build_year_from');
                }
                current.delete('page');
                return current;
            });
        },
        [setSearchParams],
    );
    const onSetBuildYearTillCallback = useCallback(
        (value: SingleValue<BuildYearOption>) => {
            setSearchParams((current) => {
                if (value?.value) {
                    current.set('build_year_till', value.value.toString());
                } else {
                    current.delete('build_year_till');
                }
                current.delete('page');
                return current;
            });
        },
        [setSearchParams],
    );

    return {
        buildYearFromOptions,
        buildYearTillOptions,
        selectedBuildYearFromOption,
        selectedBuildYearTillOption,
        onSetBuildYearFromCallback,
        onSetBuildYearTillCallback,
    };
}
